// src/pages/Menu/Vinculation/OtherCRM/MeetingCardExternal.jsx
import React, { useMemo } from 'react';
import { Calendar, Trash2, ExternalLink, FileText, MapPin } from 'lucide-react';
import Swal from 'sweetalert2';
import { otherCrmDeleteMeeting } from '../../../../services/otherCRMService';

const parseDateToLocal = (dateString) => {
  if (!dateString) return null;
  const cleanDateString = dateString.split('T')[0];
  const parts = cleanDateString.split('-');
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  return new Date(year, month - 1, day);
};

const onlyDate = (iso) => {
  if (!iso) return '—';
  const d = parseDateToLocal(iso);
  if (!d || Number.isNaN(+d)) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

export default function MeetingCardExt({
  meeting,
  minutes = [],
  entityId,          // NUEVO: id de la entidad externa (preferente)
  companyRut,        // opcional: compat
  busy,              // opcional
  onOpenDetails,     // abre el modal de detalle
  onUpdated,         // no se usa aquí
  onDeleted,
  onMinutesAdded,    // no se usa aquí
  onMinuteDeleted,   // no se usa aquí
}) {
  const mm = useMemo(() => minutes || [], [minutes]);

  const firstUrl = useMemo(() => {
    if (!mm.length) return null;
    const d = mm[0];
    return d.viewUrl || d.downloadUrl || d.fileSecureUrl || d.fileUrl || null;
  }, [mm]);

  const handleDelete = async (e) => {
    e.stopPropagation();
    const ask = await Swal.fire({ title: 'Eliminar reunión', icon: 'warning', showCancelButton: true });
    if (!ask.isConfirmed) return;
    try {
      // Prioriza entityId; companyRut queda para compat si el backend lo acepta
      await otherCrmDeleteMeeting(meeting.id, companyRut, entityId);
      onDeleted?.(meeting.id);
    } catch (err) {
      Swal.fire('Error', err?.message || 'No se pudo eliminar la reunión', 'error');
    }
  };

  const categoryColors = {
    'ACERCAMIENTO': 'bg-blue-100 text-blue-700 border-blue-200',
    'VINCULACION': 'bg-purple-100 text-purple-700 border-purple-200',
    'PROYECTO': 'bg-green-100 text-green-700 border-green-200',
    'OTRA': 'bg-gray-100 text-gray-700 border-gray-200',
  };
  const categoryColor = categoryColors[String(meeting.category || 'OTRA').toUpperCase()] || categoryColors['OTRA'];

  return (
    <li className="group rounded-xl border border-gray-200 bg-white overflow-hidden hover:shadow-md hover:border-[#0488AF]/30 transition-all duration-200">
      <div
        role="button"
        tabIndex={0}
        onClick={onOpenDetails}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpenDetails?.()}
        className="p-4 cursor-pointer"
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Calendar className="w-4 h-4 text-[#0488AF] shrink-0" />
            <div className="font-medium text-sm text-[#0b3a66] group-hover:text-[#0488AF] transition-colors">
              {onlyDate(meeting.date)}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-md border ${categoryColor} whitespace-nowrap min-w-fit`} title={String(meeting.category || 'OTRA').toUpperCase()}>
              {String(meeting.category || 'OTRA').toUpperCase()}
            </span>
          </div>
        </div>

        <h3 className="font-semibold text-base text-gray-900 group-hover:text-[#0488AF] transition-colors mb-1.5">
          {meeting.title || 'Sin título'}
        </h3>

        {meeting.description && (
          <div className="text-sm text-gray-600 line-clamp-2 mb-2 leading-relaxed">
            {meeting.description}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
          {meeting.location && (
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              <span className="truncate">{meeting.location}</span>
            </div>
          )}
          {mm.length > 0 && (
            <div className="flex items-center gap-1">
              <FileText className="w-3 h-3" />
              <span>{mm.length} {mm.length === 1 ? 'minuta' : 'minutas'}</span>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
